import React, { useState, useContext } from "react";
import { Badge, Button } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { Phone, Mail, Edit2, Info, UserCheck } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStethoscope, faUserMd, faPager } from "@fortawesome/free-solid-svg-icons";

import { UserSettingsContext } from "../../contexts/UserSettingsContext";
import ContractTypeIcon from "../../utils/ContractTypeIcon";
import { CopyChip, CopyText, NotesButton, SectionNav, SpecialtyPill, StickyHeader, UpdatedInfo } from "../header/HeaderParts";

const MAX_PRACTICES = 3;

export const getInitials = (data) => {
  const first = data.firstName ? data.firstName.trim().charAt(0) : "";
  const last = data.lastName ? data.lastName.trim().charAt(0) : "";
  return (first + last).toUpperCase() || "?";
};

export const ProviderAvatar = ({ data, size = 56 }) => {
  if (data.photoUrl) {
    return <img src={data.photoUrl} width={size} height={size} className="rounded-circle ph-avatar-img" alt="Avatar" />;
  }
  return (
    <div className="ph-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {getInitials(data)}
    </div>
  );
};

const formatDate = (dateString) => {
  if (dateString == null) {
    return "";
  }
  const parts = dateString.split("-");
  // Avoid the UTC->local timezone translation issue with js
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString("en-US");
};

const PracticeChip = ({ practice, canLink, canViewInfo, onShowInfo }) => {
  const start = formatDate(practice.startDate);
  const end = formatDate(practice.endDate);
  const dates = [start, end].join(" – ").trim();

  return (
    <span className="ph-chip" title={"Start date: " + (start || "(n/a)") + "   End date: " + (end || "(n/a)")}>
      {practice.isPrimary && <UserCheck size={13} className="ph-chip-primary" />}
      {canLink ? <a href={"/practice/" + practice.id}>{practice.name}</a> : <span>{practice.name}</span>}
      <ContractTypeIcon contractType={practice.contractType} />
      <span className="ph-chip-dates">· {dates}</span>
      {canViewInfo && (
        <Info size={13} className="ph-chip-info" onClick={() => onShowInfo(practice)} />
      )}
    </span>
  );
};

const ProviderLinks = ({ icon, label, providers }) => (
  <div className="ph-line">
    <span className="ph-supervising">
      <FontAwesomeIcon icon={icon} />
      <span className="ph-supervising-label">{label}</span>
      {providers.map((provider, i) =>
        <React.Fragment key={provider.id}>
          {i > 0 && <span className="ph-sep">·</span>}
          <a href={"/provider/" + provider.id}>{provider.name}</a>
          {(provider.states || []).slice().sort().map((state) => <span key={state} className="ph-cc ph-supervising-state">{state}</span>)}
        </React.Fragment>
      )}
    </span>
  </div>
);

const ProviderHeader = ({ data, canEdit, isUploadingPhoto, onEditClicked, onShowPracticeInfo, sections, onboardingStatus }) => {
  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const [showAllPractices, setShowAllPractices] = useState(false);

  const canLink = isH3Admin || authorities.includes("PRACTICE_BASIC_EDIT") || authorities.includes("PRACTICE_BASIC_VIEW");
  const canViewInfo = isH3Admin || authorities.includes("PRACTICE_SENSITIVE_DETAILS_VIEW") || authorities.includes("PRACTICE_SENSITIVE_DETAILS_EDIT");

  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
  const supervisors = (data.supervisingProviders || []).slice().sort(byName);
  // Supervised providers are read-only; a provider can be both supervising and supervised
  const supervised = (data.supervisedProviders || []).slice().sort(byName);

  // First, middle and last name each copy on their own
  const name = (
    <>
      <CopyText text={data.firstName} label="first name" />
      {data.nickname ? " \"" + data.nickname + "\"" : ""}
      {data.middleName && <> <CopyText text={data.middleName} label="middle name" /></>}
      {data.lastName && <> <CopyText text={data.lastName} label="last name" /></>}
      {data.credentials && data.credentials !== "" && <>, <CopyText text={data.credentials} label="credentials" /></>}
    </>
  );

  // Primary practice(s) first; sort is stable so the existing order is otherwise kept
  const practices = (data.practices || []).slice().sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
  // Past every practice's end date (a practice with no end date is still active)
  const todayIso = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local time
  const isPastAllEndDates = practices.length > 0 && practices.every((practice) => practice.endDate && practice.endDate < todayIso);
  const lastEndDate = isPastAllEndDates ? formatDate(practices.map((practice) => practice.endDate).sort().pop()) : "";

  const visiblePractices = showAllPractices ? practices : practices.slice(0, MAX_PRACTICES);
  const hiddenCount = practices.length - MAX_PRACTICES;

  // Same parts as the full name above
  const slimTitle = [data.firstName, data.nickname ? "\"" + data.nickname + "\"" : "", data.middleName, data.lastName].filter(Boolean).join(" ")
    + (data.credentials ? ", " + data.credentials : "");
  const slimSubtitle = data.specialties && data.specialties.length > 0 ? data.specialties[0].name : "";

  return (
    <StickyHeader
      avatar={<ProviderAvatar data={data} size={32} />}
      title={slimTitle}
      subtitle={slimSubtitle}
      extra={<SectionNav sections={sections} compact />}
      inactive={isPastAllEndDates}
    >
    <div className={"provider-header" + (isPastAllEndDates ? " provider-header-ended" : "")}>
      <div className="ph-avatar-wrap">
        {isUploadingPhoto ? <div className="ph-avatar"><Spinner animation="border" size="sm" /></div> : <ProviderAvatar data={data} />}
      </div>

      <div className="ph-main">
        <div className="ph-line">
          <h1 className="h3 ph-name">{name}{onboardingStatus}</h1>
          {data.pcp && <Badge bg="primary" pill>PCP</Badge>}
          {data.specialties && data.specialties.map((specialty) => <SpecialtyPill key={specialty.id} specialty={specialty} />)}
        </div>

        {supervisors.length > 0 && data.canHaveSupervising &&
          <ProviderLinks icon={faStethoscope} label="Supervising:" providers={supervisors} />
        }

        {supervised.length > 0 &&
          <ProviderLinks icon={faUserMd} label="Supervises:" providers={supervised} />
        }

        <div className="ph-line ph-contacts">
          {data.phone1 && <CopyChip label="Cell phone" value={data.phone1} icon={<Phone size={14} />} />}
          {data.pager && <CopyChip label="Pager" value={data.pager} icon={<FontAwesomeIcon icon={faPager} />} />}
          {data.email1 && <CopyChip label="Work email" value={data.email1} icon={<Mail size={14} />} />}
          {data.adminEmail && <CopyChip label="Admin CC email" value={data.adminEmail} icon={<span className="ph-cc">CC</span>} />}
        </div>

        {practices.length > 0 &&
          <div className="ph-line ph-practices">
            {visiblePractices.map((practice) =>
              <PracticeChip key={practice.id} practice={practice} canLink={canLink} canViewInfo={canViewInfo} onShowInfo={onShowPracticeInfo} />
            )}
            {hiddenCount > 0 &&
              <button type="button" className="ph-chip ph-chip-more" onClick={() => setShowAllPractices(!showAllPractices)}>
                {showAllPractices ? "Show less" : "+" + hiddenCount + " More"}
              </button>
            }
          </div>
        }
      </div>

      <div className="ph-side">
        {isPastAllEndDates && <span className="ph-ended-badge">Ended {lastEndDate}</span>}
        <div className="ph-actions">
          <SectionNav sections={sections} />
          <NotesButton notes={data.notes} />
          {canEdit &&
            <Button size="sm" variant="primary" onClick={onEditClicked}>
              <Edit2 size={14} /> Edit
            </Button>
          }
        </div>
        <UpdatedInfo updated={data.updated} updatedBy={data.updatedBy} />
      </div>
    </div>
    </StickyHeader>
  );
};

export default ProviderHeader;
